import React from "react";
import { useTranslation } from "react-i18next";
import { useCollectorsClients } from "../hooks/useCollectorsClients";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@shared/components/ui/card";
import { Separator } from "@shared/components/ui/separator";
import { Users, User, Store } from "lucide-react";
import ThreeDotLoader from "@shared/components/ThreeDotLoader";
import { Button } from "@shared/components/ui/button";
import AssignCollectorDialog from "@/features/users/components/AssignCollector";

const CollectorsClientsPage = () => {
  const { t } = useTranslation();
  const [selectedClient, setSelectedClient] = React.useState(null);
  const [showAssignDialog, setShowAssignDialog] = React.useState(false);

  const { data, isLoading } = useCollectorsClients();

  if (isLoading)
    return (
      <div className="w-full flex items-center justify-center">
        <ThreeDotLoader />
      </div>
    );

  const groups = data?.data || [];

  return (
    <div className="space-y-4">
      <h1 className="text-2xl font-bold">{t("admin.collectorsAndClients")}</h1>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {groups.map((g) => (
          <Card
            key={String(g.collectorId) || "unassigned"}
            className="hover:shadow"
          >
            <CardHeader>
              <CardTitle className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Users className="w-5 h-5" />
                  <div>
                    {g.collector ? (
                      <div className="font-medium">
                        {g.collector.brandName || g.collector.fullName}
                      </div>
                    ) : (
                      <div className="font-medium text-muted-foreground">
                        {t("livreur.unassigned")}
                      </div>
                    )}
                    <div className="text-sm text-muted-foreground">
                      {g.count} {t("admin.clients")}
                    </div>
                  </div>
                </div>
                <div>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      setSelectedClient(null); // view collector profile in future
                      alert(t("admin.openCollectorProfileToBeImplemented"));
                    }}
                  >
                    {t("common.view")}
                  </Button>
                </div>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <Separator />
              <div className="mt-2 space-y-2">
                {g.clients.map((c) => (
                  <div
                    key={c.id}
                    className="p-2 border rounded flex items-center justify-between"
                  >
                    <div>
                      <div className="font-medium">{c.brandName}</div>
                      <div className="text-sm text-muted-foreground">
                        {c.fullName} • {c.email}
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="text-sm text-muted-foreground">
                        {c.isActive ? t("status.active") : t("status.inactive")}
                      </div>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          setSelectedClient(c);
                          setShowAssignDialog(true);
                        }}
                      >
                        {t("pickup.reassign")}
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
      {selectedClient && (
        <AssignCollectorDialog
          clientUser={{ ...selectedClient }}
          open={showAssignDialog}
          onOpenChange={(open) => setShowAssignDialog(open)}
        />
      )}
    </div>
  );
};

export default CollectorsClientsPage;
